import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import styled from "styled-components";
import axios from "axios";
import {
  AppRemixIcon,
  AppCard,
  AppInput,
  AppNoInfo,
  AppCheckbox,
  AppModal,
  AppNotificationAlert,
  AppSingleSelect,
} from "@/layout/AppComponents";
import * as Yup from "yup";
import ReactDOMServer from "react-dom/server";
import Translator from "../../../layout/components/helpers/Translator";
import * as currentPageAction from "../../../store/actions/currentPageAction";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import UserManagementModuleCard from "../../../layout/screenSpecifics/Modules/UserManagement/UserManagementModuleCard";
import SelectLanguageWithSortSystem from "../../../layout/components/form/Selects/SelectLanguageWithSortSystem";
import { axiosPath, path } from "../../../routing/helpers/router";
import SpinnerLoader from "../../../layout/components/helpers/SpinnerLoader";
import UserManagementModuleSidebar from "../../../layout/screenSpecifics/Modules/UserManagement/UserManagementModuleSidebar";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import AppSpinner from "../../../layout/components/AppSpinner";
import colors from "../../../constants/colors";
import _ from "lodash";
import errorHandler from "@/layout/components/error/ErrorHandler";
import UserManagementBase from "./UserManagementBase";

export default function UserManagementAddScreen() {
  const { moduleBridgeHex, userName } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened,
  );
  const permissions = useSelector((state) => state.auth?.permissions?.client);
  const modulePermissions = useSelector(
    (s) => s.module[moduleBridgeHex]?.permissions,
  );
  const templateOptions = useSelector((s) => s.currentPage?.permissionTemplates);
  const [moduleBridge, setModuleBridge] = useState();
  const [user, setUser] = useState();
  const [modules, setModules] = useState();
  const [defaultModules, setDefaultModules] = useState();
  const [emailDomains, setEmailDomains] = useState();
  const [validationSchema] = useState(
    Yup.object().shape({
      name: Yup.string().required(
        ReactDOMServer.renderToString(
          <Translator file="module-userManagement" name="module.um.enter-name" />,
        ),
      ),
      user: Yup.string()
        .required(
          ReactDOMServer.renderToString(
            <Translator file="module-userManagement" name="module.um.enter-name" />,
          ),
        )
        .test(
          "check-user",
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.username-not-available"
            />,
          ),
          async (value) => {
            if (!value) return false;
            const response = await axios.post(
              axiosPath("api_r_user_module_userManagement_user_check"),
              {
                user: value,
              },
            );
            return response.data;
          },
        ),
      email: Yup.string()
        .required(
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.enter-valid-email-address"
            />,
          ),
        )
        .email(
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.enter-valid-email-address"
            />,
          ),
        )
        .test(
          "check-domain",
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.domain-not-supported"
            />,
          ),
          function (v) {
            if (this.parent.emailDomains === false) return true;

            if (this.parent.emailDomains === "unauthorized" || !v) return false;

            const tempEmail = this.parent.email?.split("@");
            return this.parent.emailDomains.includes(tempEmail[1]);
          },
        ),
      confirmEmail: Yup.string()
        .required(
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.enter-valid-email-address"
            />,
          ),
        )
        .email(
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.enter-valid-email-address"
            />,
          ),
        )
        .test(
          "check-mail2",
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.email-does-not-match"
            />,
          ),
          function (v) {
            if (!v) return false;
            return this.parent.email === v;
          },
        )
        .test(
          "check-domain",
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.domain-not-supported"
            />,
          ),
          function (v) {
            if (this.parent.emailDomains === false) return true;

            if (this.parent.emailDomains === "unauthorized" || !v) return false;

            const tempEmail = this.parent.email?.split("@");
            return this.parent.emailDomains.includes(tempEmail[1]);
          },
        ),
    }),
  );

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  const loadUser = useCallback(async () => {
    await axios
      .post(axiosPath("api_r_user_module_userManagement_user_data_list"), {
        moduleBridgeHex,
        userName,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          setModuleBridge(data.moduleBridge);
          setUser(data.user);
          setModules(data.modules);
          setDefaultModules(data.defaultModules);
          setEmailDomains(data.emailDomains);
          if (
            modulePermissions?.userManagement?.permissionTemplates &&
            modulePermissions?.userManagement?.permissionTemplates !== false
          )
            dispatch(
              currentPageAction.change(
                "permissionTemplates",
                data.permissionTemplates ?? [],
                true,
              ),
            );
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, user, modulePermissions]);

  useEffect(() => {
    if (modulePermissions) loadUser();
  }, [modulePermissions]);

  const handleAddUser = useCallback(
    async (payload) =>
      await axios
        .put(axiosPath("api_r_user_module_userManagement_user_add"), {
          moduleBridgeHex,
          userID: user.id,
          payload,
        })
        .then((r) => {
          if (isMounted.current) {
            if (r?.data?.message) {
              AppNotificationAlert({
                title: ReactDOMServer.renderToString(
                  <Translator
                    file="module-userManagement"
                    name="module.um.added-user"
                  />,
                ),
                message: r.data.message,
                type: "success",
                duration: 5000,
              });
            }
            navigate(
              path({
                name: "user_module_userManagement_home",
                params: {
                  moduleBridgeHex,
                },
              }),
            );
          }
        })
        .catch((e) => {
          if (e.data === "emailError") {
            AppNotificationAlert({
              title: "Error",
              message: ReactDOMServer.renderToString(
                <Translator
                  file="module-userManagement"
                  name="module.um.unauthorized-email"
                />,
              ),
              type: "danger",
            });
          } else {
            dispatch(errorHandler(e));
          }
        }),
    [moduleBridgeHex, user],
  );

  const handleCheckAllEmailNotification = useCallback((valuesModules, state) => {
    const tempValuesModules = { ...valuesModules };
    _.map(tempValuesModules, (m, i) => {
      if (_.isObject(m)) {
        m.emailNotification = state;
      }
    });

    return tempValuesModules;
  }, []);

  return (
    <Container>
      <BreadcrumbHandler
        paths={
          moduleBridge
            ? [
                {
                  name: moduleBridge?.name,
                  route: {
                    name: "user_module_userManagement_home",
                    params: { moduleBridgeHex },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-userManagement"
                      name="module.um.user-add"
                    />,
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <UserManagementBase />
      <UserManagementModuleSidebar isUserAdd permissionLevel={1} />

      <div className="row">
        <main role="main" className="col-md-12 col-lg-12">
          <div className={`${isSidebarOpened && "sidebar-expanded"}`}>
            <div className="pl-3">
              {defaultModules && (
                <Formik
                  initialValues={{
                    referenceUserID: user?.id || "",
                    name: "",
                    user: "",
                    email: "",
                    confirmEmail: "",
                    newsletterNotification: true,
                    languages: user?.lang,
                    modules: defaultModules,
                    template: null,
                    emailDomains,
                  }}
                  validationSchema={validationSchema}
                  validateOnChange={false}
                  validateOnBlur={false}
                  enableReinitialize
                  onSubmit={(v) => handleAddUser(v)}
                >
                  {({
                    setFieldValue,
                    handleSubmit,
                    values,
                    errors,
                    isSubmitting,
                  }) => (
                    <form onSubmit={handleSubmit}>
                      <AppCard
                        header={ReactDOMServer.renderToString(
                          <Translator
                            file="module-userManagement"
                            name="module.um.user-add"
                          />,
                        )}
                      >
                        {permissions?.global?.sso && (
                          <div className="row">
                            <div className="col-12">
                              <div className="alert alert-info">
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.active.directory.text"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {modules ? (
                          <div className="row">
                            <div className="col-6">
                              <h3>
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.selected-reference-user"
                                />
                              </h3>
                              <span>
                                {user?.user} ({user?.name})
                              </span>

                              <h3 className="mt-3">
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.new-user-data"
                                />
                              </h3>
                              <AppInput
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-userManagement"
                                    name="module.name.and.surname"
                                  />,
                                )}
                                icon={<AppRemixIcon icon="font-color" noVariants />}
                                onChange={(value) => setFieldValue("name", value)}
                                value={values.name}
                                isClearable
                                error={errors.name}
                              />
                              <AppInput
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-userManagement"
                                    name="module.um.username"
                                  />,
                                )}
                                icon={<AppRemixIcon icon="user" />}
                                onChange={(value) => setFieldValue("user", value)}
                                value={values.user}
                                isClearable
                                error={errors.user}
                              />
                              <AppInput
                                icon={<AppRemixIcon icon="mail" />}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-userManagement"
                                    name="module.um.email"
                                  />,
                                )}
                                value={values.email}
                                onChange={(value) => setFieldValue("email", value)}
                                error={errors.email}
                              />
                              <AppInput
                                icon={<AppRemixIcon icon="mail" />}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-userManagement"
                                    name="module.um.confirm-email"
                                  />,
                                )}
                                value={values.confirmEmail}
                                onChange={(value) =>
                                  setFieldValue("confirmEmail", value)
                                }
                                error={errors.confirmEmail}
                              />

                              <AppCheckbox
                                value={values.newsletterNotification}
                                onChange={(v) =>
                                  setFieldValue("newsletterNotification", v)
                                }
                                hasBorder
                              >
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.receive-newsletters-email"
                                />
                              </AppCheckbox>
                            </div>

                            {values.languages.length > 0 && (
                              <div className="col-6">
                                <SelectLanguageWithSortSystem
                                  value={values.languages}
                                  onChange={(opt) => setFieldValue("languages", opt)}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <AppNoInfo
                            icon={<SpinnerLoader />}
                            text={ReactDOMServer.renderToString(
                              <Translator
                                file="module-userManagement"
                                name="module.um.loading"
                              />,
                            )}
                          />
                        )}
                      </AppCard>
                      {modulePermissions?.userManagement?.permissionTemplates &&
                        modulePermissions?.userManagement?.permissionTemplates !==
                          false && (
                          <AppCard>
                            <AppSingleSelect
                              icon={<AppRemixIcon icon="apps-2" />}
                              options={templateOptions || []}
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="admin"
                                  name="general.permission.templates"
                                />,
                              )}
                              isLoading={!templateOptions}
                              value={values.template}
                              error={errors.template}
                              onChange={(value) => {
                                setFieldValue("template", value);
                              }}
                              isClearable
                            />
                          </AppCard>
                        )}
                      <AppCard
                        className="mt-3"
                        header={
                          <div>
                            <span>
                              <Translator
                                file="module-userManagement"
                                name="module.um.select-modules-user-will-used"
                              />
                            </span>
                            <span className="float-right  mr-3">
                              <span
                                className="mr-2 pointer text-primary"
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-userManagement"
                                    name="module.um.select-all-installations-notification-email"
                                  />,
                                )}
                                onClick={(v) =>
                                  setFieldValue(
                                    "modules",
                                    handleCheckAllEmailNotification(
                                      values.modules,
                                      true,
                                    ),
                                  )
                                }
                              >
                                <AppRemixIcon icon="mail-check" filled size={20} />
                              </span>
                              <span
                                className="mr-4 pointer text-primary"
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-userManagement"
                                    name="module.um.unselect-all-installations-notification-email"
                                  />,
                                )}
                                onClick={(v) =>
                                  setFieldValue(
                                    "modules",
                                    handleCheckAllEmailNotification(
                                      values.modules,
                                      false,
                                    ),
                                  )
                                }
                              >
                                <AppRemixIcon icon="mail-close" filled size={20} />
                              </span>

                              <span
                                className="mr-2 pointer text-primary"
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-userManagement"
                                    name="module.um.select-all-default-installations"
                                  />,
                                )}
                                onClick={() =>
                                  setFieldValue("modules", defaultModules)
                                }
                              >
                                <AppRemixIcon icon="checkbox" size={20} />
                              </span>

                              <span
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-userManagement"
                                    name="module.um.unselect-all-installations"
                                  />,
                                )}
                                className="mr-2 pointer text-primary"
                                onClick={() => setFieldValue("modules", {})}
                              >
                                <AppRemixIcon icon="checkbox-blank" size={20} />
                              </span>
                            </span>
                          </div>
                        }
                      >
                        {modules ? (
                          <div
                            className={`${
                              errors.modules ? "border border-danger" : undefined
                            }`}
                          >
                            {modules?.map((module) => (
                              <UserManagementModuleCard
                                key={module.hex}
                                module={module}
                                values={values}
                                setFieldValue={setFieldValue}
                              />
                            ))}

                            <div className="mt-3 float-right">
                              <button
                                type="submit"
                                className="btn btn-xs btn-outline-light"
                                disabled={isSubmitting}
                              >
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.user-add"
                                />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <AppNoInfo
                            icon={<SpinnerLoader />}
                            text={ReactDOMServer.renderToString(
                              <Translator
                                file="module-userManagement"
                                name="module.um.user-add"
                              />,
                            )}
                          />
                        )}
                      </AppCard>

                      <AppModal show={isSubmitting} title="Actualizando usuario">
                        <div className="text-center">
                          <div>
                            <AppSpinner>
                              <AppRemixIcon
                                icon="loader-4"
                                size={40}
                                color={colors.primary}
                              />
                            </AppSpinner>
                          </div>
                          <div>Realizando la petición. Por favor, espere</div>
                        </div>
                      </AppModal>
                    </form>
                  )}
                </Formik>
              )}
            </div>
          </div>
        </main>
      </div>
    </Container>
  );
}

const Container = styled.div`
  border-radius: 3px;
  h3 {
    font-size: 13px;
    color: $primary;
    font-weight: bold;
  }
  h4 {
    font-size: 10px;
    color: $primary;
    text-transform: uppercase;
  }
`;
